import {
	NoResults,
	Spinner
} from "client/Components";
import {
	normalizeSearchScopeIdentifier
} from "../useSearchResultsScopeTabs";
import { setTrackingId } from "client/Misc/utils";
import { ReactElement } from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	GlobalSearchCategoryResult,
	GlobalSearchCategoryResultStatus,
	SearchNodeModel,
	GlobalSearchScopeKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	AvailableActionsIdentifier,
	ScopeActions
} from "../GlobalSearch";
import { CategoryView } from "./CategoryView/CategoryView";
import styles from "./ResultView.module.less";
import { TimeZone } from "client/DateAndTime/timezones";
import { GlobalSearchNodeTabs } from "./GlobalSearchNodeTabs";
import { ContactSource } from "client/Api/Email/contacts";

export enum ResultStatus {
	Success = 0,
	Loading = 1,
	Error = 2
}

export type ResultData = {
	status: ResultStatus.Success | ResultStatus.Loading;
	results: readonly GlobalSearchCategoryResult[];
} | {
	status: ResultStatus.Error;
};

export type ResultViewProps = {
	nodes: readonly SearchNodeModel[];
	selectedNode: SearchNodeModel | null;
	onSelectedNodeChanged(node: SearchNodeModel | null): void;
	data: ResultData;
	onFocusInput(): void;
	onRetry(): void;
	availableShowMoreOptions: ReadonlyMap<AvailableActionsIdentifier, ScopeActions>;
	searchTerm: string;
	timezone: TimeZone;
	onOpenContactDetails: (id: string, source: ContactSource) => void;
	onDismiss: () => void;
	tabbableIndex: number | null;
};

export const ResultView = (props: ResultViewProps): ReactElement => {
	const {
		nodes,
		selectedNode,
		onSelectedNodeChanged,
		data,
		onFocusInput,
		onRetry,
		availableShowMoreOptions,
		searchTerm,
		timezone,
		onOpenContactDetails,
		onDismiss,
		tabbableIndex
	} = props;

	const scopeChangedHandler = (node: SearchNodeModel | null): void => {
		onSelectedNodeChanged(node);
		onFocusInput();
	};

	const renderResults = (results: readonly GlobalSearchCategoryResult[]): ReactElement => {
		let itemsCount: number = 0;

		return (
			<>
				{results.map(
					(result: GlobalSearchCategoryResult): ReactElement => {
						const navigationStartIndex: number = itemsCount;

						if (result.status === GlobalSearchCategoryResultStatus.Success) {
							itemsCount += result.items.length;
						}

						const availableActions: ScopeActions = availableShowMoreOptions
							.get(result.itemsScope.kind === GlobalSearchScopeKind.Predefined
								? result.itemsScope.predefinedKind
								: "integration-flow"
							)
								?? {};

						return (
							<CategoryView
								result={result}
								key={normalizeSearchScopeIdentifier(result.itemsScope)}
								navigationStartIndex={navigationStartIndex}
								availableActions={availableActions}
								onReload={onRetry}
								searchTerm={searchTerm}
								timezone={timezone}
								onOpenContactDetails={onOpenContactDetails}
								onDismiss={onDismiss}
								tabbableIndex={tabbableIndex}
							/>
						);
					}
				)}
			</>
		);
	};

	return (
		<div {...setTrackingId("results-view")} className={styles.root}>
			<div {...setTrackingId("header")} className={styles.header}>
				<div {...setTrackingId("navigation-bar")} className={styles.navigationBarWrapper}>
					<GlobalSearchNodeTabs
						selectedNode={selectedNode}
						onSelectedNodeChange={scopeChangedHandler}
						nodes={nodes}
						trackingId="global-search-node-tabs"
					/>
				</div>
			</div>
			<div
				{...setTrackingId("body")}
				className={styles.body}
				tabIndex={-1}
			>
				{data.status === ResultStatus.Error && (
					<NoResults
						trackingId="has-error"
						icon="gear"
						title={localize("_NotAbleToRetrieveSearchResults")}
						message={localize("_WeWereUnableToRetrieveSearchResults")}
						onRetry={onRetry}
					/>
				)}
				{(data.status === ResultStatus.Success || data.status === ResultStatus.Loading) && (
					<>
						{data.status === ResultStatus.Success && data.results.length === 0
							? (
								<NoResults
									trackingId="no-results-found"
									icon="magnifying-glass"
									title={localize("_NoSearchResults")}
									message={localize("_PleaseTrySearchingWithAnotherTerm")}
								/>
							)
							: renderResults(data.results)
						}
					</>
				)}
				{data.status === ResultStatus.Loading && (
					<div
						{...setTrackingId("busy-indicator")}
						className={styles.loadingIndicator}
					>
						<Spinner />
					</div>
				)}
			</div>
		</div>
	);
};

ResultView.displayName = "ResultView";
